import { useEffect, useMemo, useState } from "react";
import { FiX } from "react-icons/fi";
import PaginationBar from "../../../components/common/PaginationBar";
import { parkingYardMapsUrl, searchParkingYards } from "./parkingYardSearch";

function dash(value) {
  const text = String(value || "").trim();
  return text || "—";
}

function LocationLink({ yard }) {
  const href = parkingYardMapsUrl(yard);
  if (!href) return null;
  return (
    <a
      className="inv-yard-maps"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      View on Map
    </a>
  );
}

export function YardDetails({ yard }) {
  if (!yard) return null;
  return (
    <>
      <div className="inv-yard-card__name">{dash(yard.name)}</div>
      <div className="inv-yard-card__row">
        <span>Address</span>
        <strong>{dash(yard.address)}</strong>
      </div>
      <div className="inv-yard-card__row">
        <span>Area</span>
        <strong>{dash(yard.area)}</strong>
      </div>
      <div className="inv-yard-card__row">
        <span>Pincode</span>
        <strong>{dash(yard.pincode)}</strong>
      </div>
      <div className="inv-yard-card__row">
        <span>Owner Name</span>
        <strong>{dash(yard.ownerName)}</strong>
      </div>
      <div className="inv-yard-card__row">
        <span>Owner Mobile No</span>
        <strong>{dash(yard.ownerMobile || yard.phone)}</strong>
      </div>
      <div className="inv-yard-card__row">
        <span>Manager Name</span>
        <strong>{dash(yard.managerName)}</strong>
      </div>
      <div className="inv-yard-card__row">
        <span>Manager Mobile No</span>
        <strong>{dash(yard.managerPhone || yard.managerMobile)}</strong>
      </div>
      <div className="inv-yard-card__row">
        <span>Email</span>
        <strong>{dash(yard.email)}</strong>
      </div>
      <div className="inv-yard-card__row">
        <span>Yard Code</span>
        <strong>{dash(yard.yardCode)}</strong>
      </div>
      <div className="inv-yard-card__row">
        <span>Type of Yard</span>
        <strong>{dash(yard.yardType)}</strong>
      </div>
      <div className="inv-yard-card__row">
        <span>Location</span>
        <LocationLink yard={yard} />
      </div>
    </>
  );
}

export default function ParkingYardSearchSection({
  selectedYard,
  onSelect,
  required = true,
}) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [page, setPage] = useState(1);
  const [yards, setYards] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [changing, setChanging] = useState(!selectedYard);
  const [showManual, setShowManual] = useState(false);
  const [manual, setManual] = useState({
    name: "",
    address: "",
    area: "",
    pincode: "",
    phone: "",
  });
  const [manualError, setManualError] = useState("");

  useEffect(() => {
    if (selectedYard) setChanging(false);
  }, [selectedYard]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 400);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery]);

  const canSearch = debouncedQuery.length >= 2;

  useEffect(() => {
    if (!changing || !canSearch) {
      setYards([]);
      setTotal(0);
      setTotalPages(0);
      setLoading(false);
      setError("");
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError("");
    searchParkingYards({ query: debouncedQuery, page, limit: 20 })
      .then((result) => {
        if (cancelled) return;
        setYards(result.yards || []);
        setTotal(result.pagination?.total || 0);
        setTotalPages(result.pagination?.totalPages || 0);
      })
      .catch((err) => {
        if (cancelled) return;
        setYards([]);
        setTotal(0);
        setTotalPages(0);
        setError(err?.response?.data?.message || err?.message || "Failed to search parking yards.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [changing, canSearch, debouncedQuery, page]);

  const resultLabel = useMemo(() => {
    if (!changing) return "";
    if (!canSearch) return "Type at least 2 characters to search.";
    if (loading) return "Searching parking yards…";
    if (error) return error;
    if (!yards.length) return "No parking yards found";
    return `${total.toLocaleString("en-IN")} parking yard${total === 1 ? "" : "s"} found`;
  }, [changing, canSearch, loading, error, yards.length, total]);

  const handleSelect = (yard) => {
    onSelect?.(yard);
    setChanging(false);
    setQuery("");
    setYards([]);
    setShowManual(false);
  };

  const handleAddManual = () => {
    const name = String(manual.name || "").trim();
    if (!name) {
      setManualError("Enter parking yard name.");
      return;
    }
    setManualError("");
    handleSelect({
      id: "",
      name,
      address: String(manual.address || "").trim(),
      area: String(manual.area || "").trim(),
      pincode: String(manual.pincode || "").trim(),
      phone: String(manual.phone || "").trim(),
      manual: true,
    });
    setManual({ name: "", address: "", area: "", pincode: "", phone: "" });
  };

  const showSelected = selectedYard && !changing;

  return (
    <section className="inv-upload-section inv-yard-section" aria-labelledby="inv-yard-title">
      <h4 id="inv-yard-title" className="inv-upload-group-title">
        All India Parking Yard
      </h4>
      {required ? (
        <p className="inv-upload-hint">
          Search and select a parking yard, or enter the yard name manually, before submitting inventory.
        </p>
      ) : (
        <p className="inv-upload-hint">Search Parking Yard by Name, Area or Pincode</p>
      )}

          {showSelected ? (
        <div className="inv-yard-selected">
          <p className="inv-yard-selected__label">Selected Parking Yard</p>
          <YardDetails yard={selectedYard} />
          {selectedYard.manual ? (
            <p className="inv-yard-meta">Entered manually</p>
          ) : null}
          <button
            type="button"
            className="secondary-page-btn"
            onClick={() => setChanging(true)}
          >
            Change Parking Yard
          </button>
        </div>
      ) : (
        <>
          <label className="inv-yard-search">
            <span className="sr-only">Search Parking Yard by Name, Area or Pincode</span>
            <input
              type="text"
              className="inv-yard-search__input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.preventDefault();
              }}
              placeholder="Search Parking Yard by Name, Area or Pincode"
              autoComplete="off"
            />
            {query ? (
              <button
                type="button"
                className="inv-yard-search__clear"
                aria-label="Clear search"
                onClick={() => setQuery("")}
              >
                <FiX />
              </button>
            ) : null}
          </label>

          {!selectedYard && !canSearch && !loading ? (
            <p className="inv-yard-status">No parking yard selected</p>
          ) : (
            <p className={`inv-yard-status${error ? " inv-yard-status--error" : ""}`} aria-live="polite">
              {resultLabel}
            </p>
          )}

          {changing && canSearch && loading ? (
            <div className="inv-yard-empty">Searching parking yards…</div>
          ) : null}

          {changing && canSearch && error ? (
            <div className="inv-yard-empty inv-yard-empty--error">{error}</div>
          ) : null}

          {changing && canSearch && !loading && !error && yards.length > 0 ? (
            <div className="inv-yard-scroll">
              <table className="inv-yard-table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Name</th>
                    <th>Address</th>
                    <th>Area</th>
                    <th>Pincode</th>
                    <th>Owner Name</th>
                    <th>Owner Mobile No</th>
                    <th>Manager Name</th>
                    <th>Manager Mobile No</th>
                    <th>Email</th>
                    <th>Yard Code</th>
                    <th>Type of Yard</th>
                    <th>Location</th>
                  </tr>
                </thead>
                <tbody>
                  {yards.map((yard) => (
                    <tr key={yard.id}>
                      <td>
                        <button
                          type="button"
                          className="inv-yard-select"
                          onClick={() => handleSelect(yard)}
                        >
                          Select
                        </button>
                      </td>
                      <td>{yard.name || "—"}</td>
                      <td>{yard.address || "—"}</td>
                      <td>{yard.area || "—"}</td>
                      <td>{yard.pincode || "—"}</td>
                      <td>{yard.ownerName || "—"}</td>
                      <td>{yard.ownerMobile || yard.phone || "—"}</td>
                      <td>{yard.managerName || "—"}</td>
                      <td>{yard.managerPhone || yard.managerMobile || "—"}</td>
                      <td>{yard.email || "—"}</td>
                      <td>{yard.yardCode || "—"}</td>
                      <td>{yard.yardType || "—"}</td>
                      <td>
                        <LocationLink yard={yard} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <ul className="inv-yard-cards">
                {yards.map((yard) => (
                  <li key={yard.id} className="inv-yard-card">
                    <button
                      type="button"
                      className="inv-yard-select"
                      onClick={() => handleSelect(yard)}
                    >
                      Select
                    </button>
                    <YardDetails yard={yard} />
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {changing && canSearch && !loading && !error && yards.length === 0 ? (
            <div className="inv-yard-empty">
              No parking yards found. You can add this yard manually below.
            </div>
          ) : null}

          {changing && canSearch && totalPages > 1 ? (
            <PaginationBar
              page={page}
              totalPages={totalPages}
              total={total}
              loading={loading}
              onPageChange={setPage}
            />
          ) : null}

          <div className="inv-yard-manual">
            {showManual ? (
              <div className="inv-yard-manual__form">
                <p className="inv-yard-selected__label">Add parking yard manually</p>
                <label>
                  Parking Yard Name
                  <input
                    value={manual.name}
                    onChange={(e) => setManual((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g. Ram Parking Yard"
                    autoComplete="off"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddManual();
                      }
                    }}
                  />
                </label>
                <label>
                  Address
                  <input
                    value={manual.address}
                    onChange={(e) => setManual((prev) => ({ ...prev, address: e.target.value }))}
                    placeholder="Address"
                    autoComplete="off"
                  />
                </label>
                <div className="inv-yard-manual__row">
                  <label>
                    Area
                    <input
                      value={manual.area}
                      onChange={(e) => setManual((prev) => ({ ...prev, area: e.target.value }))}
                      placeholder="Area"
                      autoComplete="off"
                    />
                  </label>
                  <label>
                    Pincode
                    <input
                      value={manual.pincode}
                      onChange={(e) => setManual((prev) => ({ ...prev, pincode: e.target.value }))}
                      placeholder="Pincode"
                      inputMode="numeric"
                      autoComplete="off"
                    />
                  </label>
                </div>
                <label>
                  Phone
                  <input
                    value={manual.phone}
                    onChange={(e) => setManual((prev) => ({ ...prev, phone: e.target.value }))}
                    placeholder="Phone"
                    inputMode="tel"
                    autoComplete="off"
                  />
                </label>
                {manualError ? <p className="inv-yard-status inv-yard-status--error">{manualError}</p> : null}
                <div className="inv-yard-manual__actions">
                  <button type="button" className="inv-yard-select" onClick={handleAddManual}>
                    Add
                  </button>
                  <button
                    type="button"
                    className="secondary-page-btn"
                    onClick={() => {
                      setShowManual(false);
                      setManualError("");
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="secondary-page-btn"
                onClick={() => {
                  setShowManual(true);
                  setManual((prev) => ({
                    ...prev,
                    name: prev.name || query.trim(),
                  }));
                }}
              >
                Add parking yard manually
              </button>
            )}
          </div>

          {selectedYard && changing ? (
            <button
              type="button"
              className="secondary-page-btn"
              onClick={() => setChanging(false)}
            >
              Cancel
            </button>
          ) : null}
        </>
      )}
    </section>
  );
}
